/**
 * /settings/github — connect a project to a GitHub repository (FRIDAY-24).
 *
 * Project leads and workspace owners save the repository and release checks,
 * then copy the webhook URL and one-time secret into GitHub. Linked PRs move
 * tasks to In Review; a merged PR completes its tasks after a healthy
 * production deployment has been observed for the configured window.
 */

import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Project } from '@friday/shared/realtime'
import { api, ApiError } from '@/lib/api'
import { useWorkspaces } from '@/features/workspaces/workspaces'
import { toast } from '@/features/feedback/toast'
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog'

export interface GithubIntegration {
  id: string
  projectId: string
  repository: string
  releaseBranch: string
  environment: string
  healthUrl: string
  observationMinutes: number
  checkRunName: string | null
  version: number
  webhookPath: string
  events: string[]
  lastDelivery: { at: string; event: string | null; result: string | null } | null
  secret?: string
}

interface FormState {
  repository: string
  releaseBranch: string
  environment: string
  healthUrl: string
  observationMinutes: string
  checkRunName: string
}

const EVENT_LABELS: Record<string, string> = {
  pull_request: 'Pull requests',
  deployment_status: 'Deployment statuses',
  check_run: 'Check runs',
}

function toForm(i: GithubIntegration | null): FormState {
  return {
    repository: i?.repository ?? '',
    releaseBranch: i?.releaseBranch ?? 'main',
    environment: i?.environment ?? 'production',
    healthUrl: i?.healthUrl ?? '',
    observationMinutes: String(i?.observationMinutes ?? 15),
    checkRunName: i?.checkRunName ?? '',
  }
}

export function GithubSettingsPage() {
  const workspaces = useWorkspaces()
  return (
    <div className="space-y-5">
      <header>
        <Link to="/settings/integrations" className="text-xs text-[rgb(var(--fg-muted))] hover:underline">
          ← Integrations
        </Link>
        <h2 className="mt-1 text-base font-semibold">GitHub</h2>
        <p className="mt-1 max-w-prose text-xs leading-relaxed text-[rgb(var(--fg-muted))]">
          Connect a board to its repository. A pull request that names a task key (for example in its title)
          moves the task to In Review. After the PR is merged and deployed to production, the task moves to
          Done once the health check has passed for the observation window.
        </p>
      </header>
      {workspaces.isLoading && <p className="text-sm text-[rgb(var(--fg-muted))]">Loading…</p>}
      {workspaces.isError && <p className="text-sm text-red-600">Could not load your workspaces.</p>}
      {workspaces.data?.map((ws) => (
        <WorkspaceProjects key={ws.id} wsId={ws.id} name={ws.name} />
      ))}
    </div>
  )
}

function WorkspaceProjects({ wsId, name }: { wsId: string; name: string }) {
  const projects = useQuery({
    queryKey: ['projects', wsId],
    queryFn: () => api<Project[]>(`/api/v1/workspaces/${wsId}/projects`),
  })
  const active = (projects.data ?? []).filter((p) => !p.archivedAt)
  return (
    <section aria-label={name} className="space-y-3">
      <h2 className="text-surface-500 text-xs font-semibold uppercase tracking-wide">{name}</h2>
      {projects.isError && <p className="text-sm text-red-600">Could not load boards.</p>}
      {projects.data && active.length === 0 && <p className="text-surface-500 text-sm">No boards yet.</p>}
      {active.map((p) => (
        <ProjectGithubCard key={p.id} project={p} />
      ))}
    </section>
  )
}

export function ProjectGithubCard({ project }: { project: Pick<Project, 'id' | 'key' | 'name'> }) {
  const qc = useQueryClient()
  const queryKey = ['github-integration', project.id]
  const integration = useQuery({
    queryKey,
    queryFn: () => api<GithubIntegration | null>(`/api/v1/projects/${project.id}/github`),
    retry: false,
  })
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<FormState>(() => toForm(null))
  const [secret, setSecret] = useState<string | null>(null)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const current = integration.data ?? null

  useEffect(() => {
    if (!editing) setForm(toForm(current))
  }, [current, editing])

  const onSaved = (data: GithubIntegration) => {
    qc.setQueryData(queryKey, data)
    if (data.secret) setSecret(data.secret)
    setEditing(false)
  }
  const save = useMutation({
    mutationFn: () =>
      api<GithubIntegration>(`/api/v1/projects/${project.id}/github`, {
        method: 'PUT',
        body: {
          repository: form.repository.trim(),
          releaseBranch: form.releaseBranch.trim(),
          environment: form.environment.trim(),
          healthUrl: form.healthUrl.trim(),
          observationMinutes: Number(form.observationMinutes),
          checkRunName: form.checkRunName.trim() || null,
          version: current?.version,
        },
      }),
    onSuccess: (data) => {
      onSaved(data)
      toast(current ? 'GitHub settings saved' : 'GitHub connected', { kind: 'success' })
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : 'Could not save GitHub settings', { kind: 'error' }),
  })
  const rotate = useMutation({
    mutationFn: () =>
      api<GithubIntegration>(`/api/v1/projects/${project.id}/github/rotate-secret`, { method: 'POST' }),
    onSuccess: (data) => {
      onSaved(data)
      toast('New secret created. Update it in GitHub.', { kind: 'success' })
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not rotate the secret', { kind: 'error' }),
  })
  const disconnect = useMutation({
    mutationFn: () => api(`/api/v1/projects/${project.id}/github`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.setQueryData(queryKey, null)
      setSecret(null)
      setConfirmDisconnect(false)
      toast('GitHub disconnected', { kind: 'success' })
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not disconnect', { kind: 'error' }),
  })

  const forbidden = integration.error instanceof ApiError && integration.error.status === 403
  const headingId = `github-${project.id}`
  const webhookUrl = current ? `${window.location.origin}${current.webhookPath}` : ''

  return (
    <article className="card p-4" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={headingId} className="text-sm font-semibold">
          {project.name} <span className="text-surface-500 font-normal">{project.key}</span>
        </h3>
        {current && !editing && (
          <span className="rounded-full bg-green-600/10 px-2 py-0.5 text-xs text-green-700 dark:text-green-400">
            Connected to {current.repository}
          </span>
        )}
      </div>

      {integration.isLoading && <p className="text-surface-500 mt-2 text-sm">Loading…</p>}
      {forbidden && (
        <p className="text-surface-500 mt-2 text-sm">
          Only board leads and workspace owners can connect GitHub.
        </p>
      )}
      {integration.isError && !forbidden && (
        <p className="mt-2 text-sm text-red-600">Could not load GitHub settings.</p>
      )}

      {integration.isSuccess && !current && !editing && (
        <button type="button" className="btn-primary mt-3 min-h-11" onClick={() => setEditing(true)}>
          Connect GitHub
        </button>
      )}

      {integration.isSuccess && editing && (
        <form
          className="mt-3 space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
        >
          <Field label="Repository" hint="owner/name, e.g. thriv-es/friday">
            <input
              className="input w-full"
              required
              value={form.repository}
              onChange={(e) => setForm({ ...form, repository: e.target.value })}
              placeholder="owner/repository"
              autoComplete="off"
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Release branch" hint="Merges into this branch are tracked to production.">
              <input
                className="input w-full"
                required
                value={form.releaseBranch}
                onChange={(e) => setForm({ ...form, releaseBranch: e.target.value })}
              />
            </Field>
            <Field label="Production environment" hint="The GitHub deployment environment name.">
              <input
                className="input w-full"
                required
                value={form.environment}
                onChange={(e) => setForm({ ...form, environment: e.target.value })}
              />
            </Field>
          </div>
          <Field
            label="Health check URL"
            hint='https URL that returns JSON. It fails on a non-2xx status, ok=false, or any field set to "error".'
          >
            <input
              className="input w-full"
              type="url"
              required
              value={form.healthUrl}
              onChange={(e) => setForm({ ...form, healthUrl: e.target.value })}
              placeholder="https://example.com/api/v1/healthz"
            />
          </Field>
          <Field
            label="Deployment check run (optional)"
            hint="Cloudflare Pages reports production deploys as a check run, not a GitHub deployment. Enter its name, e.g. Cloudflare Pages. Leave blank to use GitHub deployment statuses only."
          >
            <input
              className="input w-full"
              value={form.checkRunName}
              onChange={(e) => setForm({ ...form, checkRunName: e.target.value })}
              placeholder="Cloudflare Pages"
            />
          </Field>
          <Field
            label="Observation window (minutes)"
            hint="Health is re-checked every 5 minutes; tasks move to Done when the window ends. 0 completes at deploy."
          >
            <input
              className="input w-32"
              type="number"
              min={0}
              max={1440}
              required
              value={form.observationMinutes}
              onChange={(e) => setForm({ ...form, observationMinutes: e.target.value })}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <button type="submit" className="btn-primary min-h-11" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : current ? 'Save changes' : 'Connect'}
            </button>
            <button type="button" className="btn-ghost min-h-11" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {current && !editing && (
        <div className="mt-3 space-y-3">
          {secret && (
            <div
              role="status"
              className="rounded-md border border-amber-400 bg-amber-50 p-3 text-sm dark:bg-amber-500/10"
            >
              <p className="font-medium">Copy this secret into GitHub now. It is not shown again.</p>
              <CopyRow label="Secret" value={secret} />
            </div>
          )}
          <div className="bg-surface-50 dark:bg-surface-800/60 space-y-2 rounded-md p-3 text-sm">
            <p className="font-medium">
              In GitHub, open{' '}
              <a
                href={`https://github.com/${current.repository}/settings/hooks/new`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 underline"
              >
                {current.repository} → Settings → Webhooks → Add webhook
              </a>{' '}
              and enter:
            </p>
            <CopyRow label="Payload URL" value={webhookUrl} />
            <CopyRow label="Content type" value="application/json" />
            <p className="text-surface-500 text-xs">
              Secret: {secret ? 'the value above' : 'the secret shown when you connected or last rotated it'}.
              Events: choose “Let me select individual events” and tick{' '}
              {listJoin(current.events.map((e) => EVENT_LABELS[e] ?? e))}.
            </p>
          </div>
          <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            <Detail term="Release branch" value={current.releaseBranch} />
            <Detail term="Environment" value={current.environment} />
            <Detail term="Health check" value={current.healthUrl} />
            <Detail term="Observation" value={`${current.observationMinutes} min`} />
            <Detail
              term="Deploy signal"
              value={
                current.checkRunName
                  ? `“${current.checkRunName}” check run or deployment status`
                  : 'Deployment status'
              }
            />
            <Detail
              term="Last delivery"
              value={
                current.lastDelivery
                  ? `${current.lastDelivery.event ?? 'event'} · ${current.lastDelivery.result ?? ''} · ${new Date(current.lastDelivery.at).toLocaleString()}`
                  : 'None yet. GitHub sends a ping when the webhook is added.'
              }
            />
          </dl>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-ghost min-h-11" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={rotate.isPending}
              onClick={() => rotate.mutate()}
            >
              Rotate secret
            </button>
            <button
              type="button"
              className="btn-ghost min-h-11 text-red-600"
              onClick={() => setConfirmDisconnect(true)}
            >
              Disconnect
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={() => disconnect.mutate()}
        title={`Disconnect GitHub from ${project.name}?`}
        body="Friday stops accepting this webhook. Existing pull request links stay on their tasks. Remove the webhook in GitHub too."
        confirmLabel="Disconnect"
        destructive
        busy={disconnect.isPending}
      />
    </article>
  )
}

function listJoin(items: string[]) {
  return items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-surface-600 dark:text-surface-300 block text-xs font-medium">{label}</span>
      {hint ? <span className="text-surface-500 mt-0.5 block text-[10px]">{hint}</span> : null}
      <div className="mt-1">{children}</div>
    </label>
  )
}

function Detail({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex min-w-0 gap-2">
      <dt className="text-surface-500 shrink-0">{term}</dt>
      <dd className="min-w-0 break-all">{value}</dd>
    </div>
  )
}

function CopyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-surface-500 w-28 shrink-0 text-xs">{label}</span>
      <code className="dark:bg-surface-900 min-w-0 flex-1 break-all rounded bg-white px-2 py-1 text-xs">
        {value}
      </code>
      <button
        type="button"
        className="btn-ghost min-h-11 text-xs"
        aria-label={`Copy ${label}`}
        onClick={() =>
          navigator.clipboard.writeText(value).then(
            () => toast(`${label} copied`, { kind: 'success' }),
            () => toast('Copy failed; select the text instead', { kind: 'error' }),
          )
        }
      >
        Copy
      </button>
    </div>
  )
}
